import { getLocalizedCategoryData } from '@prezly/theme-kit-core';
import { useCurrentLocale } from '@prezly/theme-kit-nextjs';
import classNames from 'classnames';
import Link from 'next/link';

import { useThemeSettings } from '@/hooks';
import { PUBLICITY_ASSETS_CATEGORY_SLUG } from '@/utils';
import type { StoryWithImage } from 'types';

import CategoriesList from '../CategoriesList';
import StoryImage from '../StoryImage';
import { StoryPublicationDate } from '../StoryPublicationDate';

import styles from './StoryCard.module.scss';

type Props = {
    story: StoryWithImage;
};

function StoryCard({ story }: Props) {
    const { categories, title, subtitle } = story;
    const { showDate, showSubtitle } = useThemeSettings();
    const currentLocale = useCurrentLocale();

    const isPublicityAssetStory = categories.some(
        (category) =>
            getLocalizedCategoryData(category, currentLocale).slug ===
            PUBLICITY_ASSETS_CATEGORY_SLUG,
    );

    return (
        <div className={styles.container}>
            <div className={styles.imageWrapper}>
                <StoryImage
                    story={story}
                    size="default"
                    className={styles.image}
                    placeholderClassName={styles.placeholder}
                />
            </div>
            <div className={styles.content}>
                {categories.length > 0 && (
                    <div className={styles.categories}>
                        <CategoriesList
                            categories={categories}
                            isStatic
                            linkClassName={styles.categoryLink}
                        />
                    </div>
                )}
                <h3
                    className={classNames(styles.title, {
                        [styles.extendedTitle]: !subtitle || !showSubtitle,
                    })}
                >
                    <Link href={`/${story.slug}`} locale={false} className={styles.titleLink}>
                        <span className={styles.linkMask} />
                        {title}
                    </Link>
                </h3>

                {subtitle && showSubtitle && <p className={styles.subtitle}>{subtitle}</p>}

                {showDate && !isPublicityAssetStory && (
                    <p className={styles.date}>
                        <StoryPublicationDate story={story} />
                    </p>
                )}
            </div>
        </div>
    );
}

export default StoryCard;
